import { utils, write } from "xlsx";
import { saveAs } from "file-saver";
import finance from "financejs";
import { numberFormat, participationsFormat } from "./numberFormat";
import { millisToDays, toSpanishDate } from "./dateFormat";
import { transformDivestments, transformInvestments } from "./finance";

export const exportToExcel = (data, columns, filename) => {
  // Convertir los datos a un array de objetos
  const dataObjects = data.map((row) => {
    const obj = {};
    columns.forEach((col, index) => {
      obj[col] = row[index];
    });
    return obj;
  });

  const worksheet = utils.json_to_sheet(dataObjects, { header: columns });
  const workbook = { Sheets: { data: worksheet }, SheetNames: ["data"] };
  const excelBuffer = write(workbook, { bookType: "xlsx", type: "array" });
  const excelBlob = new Blob([excelBuffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  saveAs(excelBlob, filename);
};

export const exportToExcelWithTabs = (tabsData, fileName) => {
  const workbook = utils.book_new();

  tabsData.forEach((tabData, index) => {
    const { rows, columns, sheetName } = tabData;
    // Convertir los datos a un array de objetos
    const dataObjects = rows.map((row) => {
      const obj = {};
      columns?.forEach((col, index) => {
        obj[col] = row[index];
      });
      return obj;
    });

    const worksheet = utils.json_to_sheet(dataObjects, { header: columns });
    utils.book_append_sheet(
      workbook,
      worksheet,
      sheetName || `Sheet${index + 1}`
    );
  });

  const excelBuffer = write(workbook, { bookType: "xlsx", type: "array" });
  const excelBlob = new Blob([excelBuffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  saveAs(excelBlob, `${fileName}.xlsx`);
};

export const transformInvestmentData = (rows, columns, t) => {
  const investmentSummary = [
    {
      sheetName: t("toExcel.generalInvestment"),
      columns: columns?.map((col) => col?.headerName),
      rows: getValuesFromRowsToGeneral(rows),
    },
  ];
  return investmentSummary.concat(convertDataToExcelFormat(rows, t));
};

export const transformDivestmentData = (rows, columns, t) => {
  const investmentSummary = [
    {
      sheetName: t("toExcel.generalDivestment"),
      columns: columns?.map((col) => col?.headerName),
      rows: getValuesFromRowsToGeneralDivestment(rows),
    },
  ];
  return investmentSummary.concat(convertDivestmentDataToExcelFormat(rows, t));
};

const getValuesFromRowsToGeneral = (rows) => {
  return rows?.map((row) => getValuesFromRowToGeneral(row));
};

const getValuesFromRowsToGeneralDivestment = (rows) => {
  return rows?.map((row) => getValuesFromRowToGeneralDivestment(row));
};

const getValuesFromRowToGeneral = (row) => {
  const amount = row?.data
    .map((invest) => invest?.amount)
    .reduce((a, b) => a + b, 0);

  const formattedAmount = numberFormat(amount);

  const res = row?.data?.map((invest) => {
    const sharesAmount = invest?.participationValuation
      ? invest?.amount / invest?.participationValuation
      : 0;
    const newCapital = sharesAmount * invest?.newAmount;
    const resp = invest?.participationValuation
      ? newCapital - invest?.amount
      : 0;
    return invest?.investmentType === "Préstamo Convertible" ? 0 : resp;
  });

  const totalResp = res?.reduce((a, b) => a + b, 0);
  const formattedTotalResp = numberFormat(totalResp);

  const url = row?.data[0].url;

  const provisionalMoic = (amount + totalResp) / amount;
  const moic =
    isNaN(provisionalMoic) || provisionalMoic === Infinity
      ? "1,00 x"
      : participationsFormat(provisionalMoic.toFixed(2)) + " x";

  const tir = () => {
    const investments = row.data.map((inv) => {
      const sharesAmount = inv.participationValuation
        ? inv.amount / inv.participationValuation
        : 0;
      const newCapital = sharesAmount * inv.newAmount;
      const resp = inv.participationValuation ? newCapital - inv.amount : 0;
      const capitalGain =
        inv.investmentType === "Préstamo Convertible"
          ? 0
          : parseInt(resp.toFixed(2));

      return {
        cashflow: inv.amount,
        investmentDate: inv.investmentDate,
        capitalGain: capitalGain === -0 ? 0 : capitalGain,
      };
    });

    const investmentsFinal = transformInvestments(investments);
    const financeInstance = new finance();
    try {
      const tir = financeInstance.XIRR(
        investmentsFinal.map((inv) => inv.cashflow),
        investmentsFinal.map((inv) => new Date(inv.investmentDate)),
        -0.25
      );

      return isNaN(tir) || tir === Infinity
        ? "0,00%"
        : participationsFormat(tir.toFixed(2)) + "%";
    } catch (error) {
      console.error("Error al calcular la TIR:", error);
      return "-";
    }
  };

  return [row?.startup, formattedAmount, formattedTotalResp, tir(), moic, url];
};

const getValuesFromRowToGeneralDivestment = (row) => {
  const amount = row.data
    .map(
      (invest) => invest.divestmentParticipation * invest.participationValuation
    )
    .reduce((a, b) => a + b, 0);

  const formattedAmount = numberFormat(amount);

  const res = row.data.map((invest) => {
    const newCapital = invest.divestmentParticipation * invest.salesPrice;
    const resp =
      newCapital -
      invest.divestmentParticipation * invest.participationValuation;
    return resp;
  });

  const totalCapitalGain = res.reduce((a, b) => a + b, 0);
  const moic = participationsFormat((amount + totalCapitalGain) / amount) + "x";

  const tir = () => {
    const investments = row.data.map((inv) => {
      const newCapital = inv.divestmentParticipation * inv.salesPrice;
      const capitalGain =
        newCapital - inv.divestmentParticipation * inv.participationValuation;

      return {
        cashflow: inv?.divestmentParticipation * inv?.participationValuation,
        investmentDate: inv.investmentDate,
        capitalGain: capitalGain === -0 ? 0 : parseInt(capitalGain.toFixed(2)),
        divestmentDate: inv.divestmentDate,
      };
    });

    const investmentsFinal = transformDivestments(investments);
    const financeInstance = new finance();
    try {
      return financeInstance.XIRR(
        investmentsFinal.map((inv) => inv.cashflow),
        investmentsFinal.map((inv) => new Date(inv.investmentDate)),
        -0.9
      );
    } catch (error) {
      console.error("Error al calcular la TIR:", error);
      return "-";
    }
  };

  const tirFinal = tir();

  return [
    row?.startup,
    formattedAmount,
    isNaN(tirFinal) || tirFinal === Infinity ? "-100,00%" : tirFinal + "%",
    moic,
    numberFormat(totalCapitalGain.toFixed(2)),
  ];
};

const convertDataToExcelFormat = (data, t) => {
  return data.map((rows) => {
    const rowsPersonalizadas = rows.data.map((row) => {
      const formattedRow = {
        dateOfReservation: toSpanishDate(row.investmentDate),
        RondroundNumbera: row.roundNumber,
        amount: numberFormat(row.amount.toFixed(2)),
        participationValuation:
          row.participationValuation === null ||
          row.investmentType === "Préstamo Convertible"
            ? "-"
            : numberFormat(row.participationValuation.toFixed(2)),
        shares: (() => {
          const resp = row.amount / row.participationValuation;
          return row.participationValuation === null ||
            row.participationValuation === Infinity ||
            row.investmentType === "Préstamo Convertible"
            ? "-"
            : participationsFormat(resp.toFixed(2));
        })(),
        newAmount:
          row.participationValuation === null ||
          row.investmentType === "Préstamo Convertible"
            ? "-"
            : numberFormat(row.newAmount),
        capitalGain: (() => {
          const sharesAmount = row.amount / row.participationValuation;
          const newCapital = sharesAmount * row.newAmount;
          const resp = newCapital - row.amount;
          return row.participationValuation === null ||
            row.investmentType === "Préstamo Convertible"
            ? "-"
            : numberFormat(resp.toFixed(2));
        })(),
        tir: (() => {
          const sharesAmount = row.amount / row.participationValuation;
          const newCapital = sharesAmount * row.newAmount;
          const capitalGain = newCapital - row.amount; // Plus

          const LN = Math.log((row.amount + capitalGain) / row.amount);
          const today = new Date();
          const diffDates =
            millisToDays(today - new Date(row.investmentDate)) / 365;

          const provisionalTir = (Math.exp(LN / diffDates) - 1) * 100;

          return isNaN(provisionalTir) || provisionalTir === Infinity
            ? "0,00%"
            : participationsFormat(provisionalTir) + "%";
        })(),
        moic: (() => {
          const sharesAmount = row.amount / row.participationValuation;
          const m2m = sharesAmount * row.newAmount;
          const provisionalMoic = m2m / row.amount;
          return isNaN(provisionalMoic) || provisionalMoic === Infinity
            ? "1,00 x"
            : participationsFormat(provisionalMoic) + "x";
        })(),
        vehicle: row.vehicle,
        whoInvests: row.whoInvests,
      };
      return Object.values(formattedRow);
    });

    return {
      sheetName: rows.startup,
      columns: [
        t("positions.dateOfReservation"),
        t("positions.roundNumber"),
        t("positions.amount"),
        t("positions.participationValuation"),
        t("positions.shares"),
        t("positions.newAmount"),
        t("positions.capitalGain"),
        t("positions.tir"),
        t("positions.moic"),
        t("positions.vehicle"),
        t("positions.whoInvests"),
      ],
      rows: rowsPersonalizadas,
    };
  });
};

const convertDivestmentDataToExcelFormat = (data, t) => {
  return data.map((rows) => {
    const rowsPersonalizadas = rows.data.map((row) => {
      const amount = row.divestmentParticipation * row.participationValuation;
      const newCapital = row.divestmentParticipation * row.salesPrice;
      const capitalGain =
        newCapital - row.divestmentParticipation * row.participationValuation;

      const moic = participationsFormat((amount + capitalGain) / amount) + "x";
      const tir = () => {
        const LN = Math.log((amount + capitalGain) / amount);
        const diffDates =
          millisToDays(
            new Date(row.divestmentDate) - new Date(row.investmentDate)
          ) / 365;

        return participationsFormat((Math.exp(LN / diffDates) - 1) * 100) + "%";
      };

      const formattedRow = {
        startup: row?.startup,
        divestmentDate: toSpanishDate(row?.divestmentDate),
        roundNumber: row?.roundNumber,
        amount: numberFormat(
          (row?.divestmentParticipation * row?.participationValuation).toFixed(
            2
          )
        ),
        participationValuation: (() => {
          const resp = row?.participationValuation;
          return resp === null ? "-" : numberFormat(resp.toFixed(2));
        })(),
        shares: participationsFormat(row?.divestmentParticipation?.toFixed(2)),
        newAmount: numberFormat(row?.salesPrice?.toFixed(2)),
        tir: tir(),
        moic: moic,
        capitalGain: (() => {
          const newCapital = row?.divestmentParticipation * row?.salesPrice;
          const resp =
            newCapital -
            row?.divestmentParticipation * row?.participationValuation;
          return numberFormat(resp?.toFixed(2));
        })(),
        vehicle: row.vehicle,
        whoInvests: row.whoInvests,
      };
      return Object.values(formattedRow);
    });

    return {
      sheetName: rows.startup,
      columns: [
        t("divestment.startup"),
        t("divestment.divestmentDate"),
        t("positions.roundNumber"),
        t("positions.amount"),
        t("positions.participationValuation"),
        t("positions.shares"),
        t("positions.newAmount"),
        t("positions.tir"),
        t("positions.moic"),
        t("positions.capitalGain"),
        t("positions.vehicle"),
        t("positions.whoInvests"),
      ],
      rows: rowsPersonalizadas,
    };
  });
};

export const createAllInvestmentLpByStartup = (
  columns,
  row,
  sheetName,
  percentage
) => {
  const [totals, ...restOfArray] = row;
  const rowFinal = restOfArray.map((startup) => {
    const myStartup = startup?.startup;
    const amount = row?.amount
      ? row.amount
      : numberFormat(startup.data.reduce((acc, obj) => acc + obj.amount, 0));
    const fundValue = row?.fundValue
      ? row.fundValue
      : startup?.data.reduce((acc, obj) => {
          const shares = obj?.amount / obj?.participationValuation;
          const value = (obj?.participationValuation && obj?.lastValoration !== null && obj?.lastValoration !== undefined)
            ? obj?.lastValoration * shares
            : obj?.amount;
          return acc + value;
        }, 0);
    const formattedFundValue =
      fundValue === 0 ? "-" : numberFormat(fundValue.toFixed(2));
    const LPCost = row?.LPCost
      ? row.LPCost
      : startup.data.reduce((acc, obj) => {
          return acc + obj.amount * (percentage / 100);
        }, 0);
    const LPValue = row?.LPValue
      ? row.LPValue
      : startup.data.reduce((acc, obj) => {
          const shares =
            (obj.amount * (percentage / 100)) / obj.participationValuation;
          const value = (obj.participationValuation && obj.lastValoration !== null && obj.lastValoration !== undefined)
            ? obj.lastValoration * shares
            : obj.amount * (percentage / 100);
          return acc + value;
        }, 0);

    const formattedLPValue =
      LPValue === 0 ? "-" : numberFormat(LPValue.toFixed(2));

    const formattedLPCost = numberFormat(LPCost.toFixed(2));

    return [
      myStartup,
      amount,
      formattedFundValue,
      formattedLPCost,
      formattedLPValue,
      startup.url,
    ];
  });

  const totalsFinal = [
    totals?.startup,
    totals?.amount,
    totals?.fundValue,
    totals?.LPCost,
    totals?.LPValue,
    "",
  ];

  return {
    columns,
    rows: [...rowFinal, totalsFinal],
    sheetName,
  };
};

export const createSheetLP = (rows, columns, percentage) => {
  // eslint-disable-next-line no-unused-vars
  const [_unused, ...restOfRow] = rows;
  const allTables = restOfRow.map((row) => {
    const internalRows = row?.data?.map((r) => {
      const finalArray = {
        investmentDate: toSpanishDate(r?.investmentDate),
        roundNumber: r?.roundNumber,
        investmentType: r?.investmentType,
        amount: numberFormat(r?.amount),
        fundValue:
          r?.fundValue !== undefined
            ? numberFormat(r?.fundValue.toFixed(2))
            : (() => {
                const shares = r?.amount / r?.participationValuation;
                const value = (r?.participationValuation && r?.lastValoration !== null && r?.lastValoration !== undefined)
                  ? r?.lastValoration * shares
                  : r?.amount;
                return numberFormat(value.toFixed(2));
              })(),
        lPCost: numberFormat((r?.amount * (percentage / 100)).toFixed(2)),
        LPValue:
          r?.LPValue !== undefined
            ? numberFormat(r?.LPValue.toFixed(2))
            : (() => {
                const shares =
                  (r?.amount * (percentage / 100)) / r?.participationValuation;
                const value = (r?.participationValuation && r?.lastValoration !== null && r?.lastValoration !== undefined)
                  ? r?.lastValoration * shares
                  : r?.amount * (percentage / 100);
                return numberFormat(value.toFixed(2));
              })(),
      };
      return Object.values(finalArray);
    });
    return {
      columns,
      rows: internalRows,
      sheetName: row?.startup,
    };
  });

  return allTables;
};
